import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  deleteAccommodation,
  fetchAllAccommodations,
  fetchAllAccommodationsOffered,
} from '../../common/api/accommodationsService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  InitialPaginationParameters,
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';
import { AutoSuggestOption } from '../autocomplete/autocompleteSlice';
import { MultiSelectAttribute } from '../searchBookableAccommodations/searchBookableAccommodationsSlice';

export interface AccommodationListItem {
  id: string;
  accommodationNumber: number;
  isDeleted: boolean;
  name: string;
  accommodationGroupName: string;
  municipality: string;
  imageUrl: string;
  place: string;
  region: string;
  skiResort: string;
  beds: number;
  extraBeds: number;
}

export interface FilterCriteria {
  autosuggestions?: AutoSuggestOption[];
  searchString: string;
  showDeleted: boolean;
  accommodationNumber: number;
  novelty: boolean;
  onSale: boolean;
  showInOwnerPage: boolean;
  interhomeService?: boolean;
  createdFrom?: Date;
  createdTo?: Date;
  accommodationGroupId: string;
  accountId: string;
  maxPersonCount?: string;
  numberOfBedrooms?: string;
  numberOfToilets?: string;
  qualityRatings: [];
  area?: number;
  minimumNumericAttributes?: Record<string, number>;
  maximumNumericAttributes?: Record<string, number>;
  booleanAttributes?: string[];
  multiSelectAttributes?: MultiSelectAttribute[];
  textAttributes?: Record<string, string>;
  tagIds?: string[];
  noPriceGroup?: boolean;
}

export interface AccommodationQueryFilters extends PaginationParameters {
  filterCriteria: FilterCriteria;
}

export interface AccommodationsSearchState {
  accommodationItems: AccommodationListItem[];
  offeredAccommodationItems: AccommodationListItem[];
  accommodationsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  offeredAccommodationsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationFilter: AccommodationQueryFilters;
  pagination: PaginationResponse;
}

export const initialState: AccommodationsSearchState = {
  accommodationItems: [],
  offeredAccommodationItems: [],
  accommodationFilter: {
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: InitialPaginationParameters.PageSize,
    filterCriteria: {
      autosuggestions: [],
      searchString: undefined,
      showDeleted: false,
      accommodationNumber: undefined,
      novelty: undefined,
      onSale: true,
      interhomeService: undefined,
      showInOwnerPage: undefined,
      accommodationGroupId: undefined,
      accountId: undefined,
      qualityRatings: [],
      minimumNumericAttributes: {},
      maximumNumericAttributes: {},
      booleanAttributes: [],
      multiSelectAttributes: [],
      textAttributes: {},
      tagIds: [],
      noPriceGroup: false,
    },
  },
  accommodationsStatus: LOADING_STATUS.IDLE,
  offeredAccommodationsStatus: LOADING_STATUS.IDLE,
  pagination: InitialPaginationResponse,
};

export const getAllAccommodationsAsync = createAsyncThunk(
  'accommodationsSearch/getAllAccommodations',
  async (filterCriteria: AccommodationQueryFilters, { rejectWithValue }) => {
    try {
      return await fetchAllAccommodations(filterCriteria);
    } catch (error) {
      console.log('error', error);
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllAccommodationsOfferedAsync = createAsyncThunk(
  'accommodationsSearch/getAllAccommodationsOffered',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAllAccommodationsOffered();
      return response;
    } catch (error) {
      console.log('error', error);
      return rejectWithValue(error.response);
    }
  },
);

export const deleteAccommodationAsync = createAsyncThunk(
  'accommodationsSearch/deleteAccommodation',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await deleteAccommodation(accommodationId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const accommodationsSearchSlice = createSlice({
  name: 'accommodationsSearch',
  initialState,
  reducers: {
    setSearchFilters(state, action: PayloadAction<AccommodationQueryFilters>) {
      state.accommodationFilter = action.payload;
    },
    resetFilters(state) {
      state.accommodationFilter = initialState.accommodationFilter;
      state.accommodationItems = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAllAccommodationsAsync.pending, state => {
        state.accommodationsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllAccommodationsAsync.fulfilled, (state, action) => {
        state.accommodationsStatus = LOADING_STATUS.IDLE;
        state.accommodationItems = action.payload.data;
        state.pagination = PopulatePagination(action.payload);
      })
      .addCase(getAllAccommodationsAsync.rejected, state => {
        state.accommodationsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllAccommodationsOfferedAsync.pending, state => {
        state.offeredAccommodationsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllAccommodationsOfferedAsync.fulfilled, (state, action) => {
        state.offeredAccommodationsStatus = LOADING_STATUS.IDLE;
        state.offeredAccommodationItems = action.payload;
      })
      .addCase(getAllAccommodationsOfferedAsync.rejected, state => {
        state.offeredAccommodationsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectAccommodations = (state: RootState) =>
  state.accommodationsSearch.accommodationItems;

export const selectOfferedAccommodations = (state: RootState) =>
  state.accommodationsSearch.offeredAccommodationItems;

export const selectAccommodationsSearchFilter = (state: RootState) =>
  state.accommodationsSearch.accommodationFilter;

export const selectAccommodationsLoadingStatus = (state: RootState) =>
  state.accommodationsSearch.accommodationsStatus;
export const selectAccommodationsPagination = (state: RootState) =>
  state.accommodationsSearch.pagination;

export const { setSearchFilters, resetFilters } =
  accommodationsSearchSlice.actions;

export default accommodationsSearchSlice.reducer;
